import { Head, useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

// Inline field-error component. Errors arriving via Inertia's `errors` prop
// (set by the controller's `redirect_to ..., inertia: { errors: { … } }`)
// flow into useForm.errors[fieldName]. Render right below the input so the
// user's eye stays on the offending field. Mirrors the FieldError in
// App/Account/Index.jsx.
function FieldError({ message }) {
  if (!message) return null
  return <p role="alert" className="text-xs text-red-600">{message}</p>
}

// task-328 U4. The waived "set a new password" step. Reached only after a
// fresh magic-link (email_auth) sign-in via the recovery flow, so there is
// deliberately NO current-password field — the emailed link was the proof.
// Minimal card layout (no public Header/Footer), matching the Auth/account
// surfaces. Placed under App/** so it is excluded from the SSR glob (see
// app/frontend/ssr/ssr.jsx) and its controller opts out of SSR too.
export default function ResetPassword() {
  const form = useForm({ password: "" })

  const submit = (event) => {
    event.preventDefault()
    // No onSuccess handler: a successful reset redirects to the role portal,
    // so this page unmounts. On failure Inertia surfaces errors and preserves
    // the entered value.
    form.patch("/account/reset-password")
  }

  return (
    <>
      <Head title="Reset your password" />
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Set a new password</CardTitle>
            <CardDescription>
              You followed a recovery link, so you're signed in. Set a new password
              below. You won't be asked for your old one.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  required
                  autoFocus
                  value={form.data.password}
                  onChange={(event) => form.setData("password", event.target.value)}
                  aria-invalid={form.errors.password ? "true" : undefined}
                />
                <p className="text-xs text-zinc-500">Minimum 8 characters.</p>
                <FieldError message={form.errors.password} />
              </div>
              <Button type="submit" className="w-full" disabled={form.processing}>
                {form.processing ? "Saving..." : "Save new password"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
